import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AITimelineGenerator } from "@/components/ai-timeline/AITimelineGenerator";
import { ArrowLeft, Wand2 } from "lucide-react";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { weddingDateText, weddingSlotText } from "@/lib/timelineDisplay";
import { pickCurrentWedding } from "@/lib/currentWedding";
import { useWeddingPackageName } from "@/hooks/useWeddingPackage";

export default function AITimelinePage() {
  const { user } = useAuth();

  // The couple's weddings. Same cache entry ('/api/weddings') and SAME SHAPE (array) as
  // every other page: this page used to store a single wedding object under this key,
  // which broke the sidebar and Home (weddings?.[0] undefined) after a refetch.
  const { data: weddings, isLoading, error } = useQuery({
    queryKey: ['/api/weddings'],
    queryFn: async () => {
      const response = await apiRequest('/api/weddings');
      const list = await response.json();
      return Array.isArray(list) ? list : [];
    },
    enabled: !!user
  });
  // Upcoming non-cancelled wedding, not whatever the server sorts first (M50).
  // Picked before the early returns so the package hook reads the SAME wedding.
  const currentWedding: any = pickCurrentWedding(Array.isArray(weddings) ? weddings : []);
  // Hooks before the early returns below.
  const packageName = useWeddingPackageName(currentWedding ?? null);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8860B]"></div>
          </div>
        </div>
      </div>
    );
  }

  const weddingObject: any = currentWedding;

  if (!weddingObject?.id) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <Link href="/">
              <Button variant="ghost" className="text-[#B8860B] hover:bg-[#B8860B]/10">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>

          <Card className="bg-black/40 border-[#B8860B]/30">
            <CardContent className="p-8 text-center">
              <Wand2 className="h-16 w-16 text-gray-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">
                {error ? "We couldn't load your booking" : "No Wedding Found"}
              </h2>
              <p className="text-gray-300 mb-6">
                {error
                  ? "Please check your connection and refresh the page."
                  : "Your day-of timeline appears here once your booking is confirmed."}
              </p>
              <Link href="/bookings">
                <Button className="bg-[#B8860B] hover:bg-[#9A7209] text-black min-h-11">
                  View My Bookings
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // IST date (en-IN, same as Home) and only real values — no made-up defaults.
  const dateText = weddingDateText(weddingObject.weddingDate);
  const slotText = weddingSlotText(weddingObject);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Navigation */}
        <div className="flex items-center mb-6">
          <Link href="/">
            <Button variant="ghost" className="text-[#B8860B] hover:bg-[#B8860B]/10">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Wedding Info Header */}
        <div className="mb-8">
          <div className="bg-black/40 rounded-lg border border-[#B8860B]/30 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 break-words">
                  {[weddingObject.brideName, weddingObject.groomName].filter(Boolean).join(" & ") || "Your Wedding"}
                </h1>
                <dl className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-3 text-sm">
                  {dateText && (
                    <div className="min-w-0">
                      <dt className="text-gray-400 text-xs">Date</dt>
                      <dd className="text-white">{dateText}</dd>
                    </div>
                  )}
                  {slotText && (
                    <div className="min-w-0">
                      <dt className="text-gray-400 text-xs">Time</dt>
                      <dd className="text-white">{slotText}</dd>
                    </div>
                  )}
                  {weddingObject.venue && (
                    <div className="min-w-0">
                      <dt className="text-gray-400 text-xs">Venue</dt>
                      <dd className="text-white break-words">{weddingObject.venue}</dd>
                    </div>
                  )}
                  {weddingObject.guestCount ? (
                    <div className="min-w-0">
                      <dt className="text-gray-400 text-xs">Guests</dt>
                      <dd className="text-white">{Number(weddingObject.guestCount).toLocaleString("en-IN")}</dd>
                    </div>
                  ) : null}
                </dl>
              </div>
              {packageName && (
                <div className="sm:text-right flex-shrink-0">
                  <div className="text-xs text-gray-400">Wedding Package</div>
                  <div className="text-lg font-semibold text-[#B8860B]">{packageName}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AI Timeline Generator */}
        <AITimelineGenerator wedding={weddingObject} />
      </div>
    </div>
  );
}
